import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState, useRef, useCallback, useMemo, memo, useEffect, type ReactNode, type ChangeEvent } from 'react'
import { AppShell } from '@/shared/ui/app-shell'
import { BottomNav } from '@/widgets/bottom-nav'
import { Textarea } from '@/shared/ui/textarea'
import { Checkbox } from '@/shared/ui/checkbox'
import { Input } from '@/shared/ui/input'
import { useTelegram, useBackButton, useMainButton } from '@/shared/lib/telegram'
import { AuthGuard } from '@/shared/lib/router'
import { useCart } from '@/features/cart/api/use-cart'
import { useCreateOrder, useOrders } from '@/features/orders/api/use-orders'
import { useUser } from '@/entities/user/api/use-user'
import { CdekDeliverySection } from '@/features/checkout/ui/cdek-delivery-section'
import { CdekDeliveryPicker } from '@/features/checkout/ui/cdek-delivery-picker'
import {
  useDeliveryStore,
  getDeliveryResult,
  selectIsCdek,
  selectIsCdekSelected,
  selectDeliveryCost,
  selectCanSubmitDelivery,
  selectWidgetOpen,
} from '@/features/checkout/model'
import { useDeliveryRecalculation } from '@/features/checkout/lib/use-delivery-recalculation'
import { Skeleton } from '@/shared/ui/skeleton'
import { useToast } from '@/shared/ui/toast'
import { ProductImage } from '@/entities/product/ui'
import { formatPrice, formatWeight } from '@/shared/lib/format'
import { calculateTeaPrice } from '@/shared/lib/pricing'
import { useSiteSettings, formatAddress } from '@/shared/api/use-settings'
import { cn } from '@/shared/lib/utils'
import {
  ChevronDown,
  ChevronUp,
  Shield,
  User,
  Phone,
  Mail,
  CreditCard,
  Banknote,
  Gift,
  Check,
  Loader2,
  X,
} from 'lucide-react'
import { apiClient } from '@/shared/api/client'

// ============================================================================
// Constants
// ============================================================================

const MAIN_BUTTON_COLORS = {
  light: { color: '#C73B0D', textColor: '#FFFFFF' },
  dark: { color: '#E84D1C', textColor: '#FFFFFF' },
}

// ============================================================================
// Route
// ============================================================================

export const Route = createFileRoute('/checkout')({
  component: CheckoutPage,
})

function CheckoutPage() {
  return (
    <AuthGuard>
      <CheckoutContent />
    </AuthGuard>
  )
}

// ============================================================================
// Main Content
// ============================================================================

function CheckoutContent() {
  const { hapticFeedback, webApp } = useTelegram()
  const navigate = useNavigate()
  const { error: showErrorToast } = useToast()

  // --- Zustand delivery state (primitive selectors — no unnecessary re-renders) ---
  const isCdek = useDeliveryStore(selectIsCdek)
  const isCdekSelected = useDeliveryStore(selectIsCdekSelected)
  const deliveryCost = useDeliveryStore(selectDeliveryCost)
  const canSubmitDelivery = useDeliveryStore(selectCanSubmitDelivery)
  const widgetOpen = useDeliveryStore(selectWidgetOpen)

  // --- Local UI state ---
  const [orderCreated, setOrderCreated] = useState(false)
  const commentRef = useRef('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [isOrderExpanded, setIsOrderExpanded] = useState(false)
  const [deliveryErrorCount, setDeliveryErrorCount] = useState(0)
  const [termsErrorCount, setTermsErrorCount] = useState(0)
  const [recipientName, setRecipientName] = useState('')
  const [recipientNameError, setRecipientNameError] = useState(false)
  const [phone, setPhone] = useState('')
  const [phoneError, setPhoneError] = useState(false)
  const [isEditingPhone, setIsEditingPhone] = useState(false)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [paymentType, setPaymentType] = useState<'online' | 'cash'>('online')

  // Gift card
  const [giftCardCode, setGiftCardCode] = useState('')
  const [giftCardAmount, setGiftCardAmount] = useState(0)
  const [giftCardBalance, setGiftCardBalance] = useState(0)
  const [giftCardApplied, setGiftCardApplied] = useState(false)
  const [giftCardError, setGiftCardError] = useState('')
  const [giftCardLoading, setGiftCardLoading] = useState(false)
  const [giftCardExpanded, setGiftCardExpanded] = useState(false)

  // --- Data ---
  const { data: cartData, isLoading: cartLoading } = useCart()
  const { data: userData } = useUser()
  const { data: settingsData } = useSiteSettings()
  const createOrder = useCreateOrder()
  const { data: ordersData } = useOrders()

  const cart = cartData?.data
  const hasCart = cart && cart.items.length > 0
  const pickupAddress = formatAddress(settingsData?.data?.address)

  // Prefill from user profile
  const userPhone = userData?.data?.phone
  const userEmail = (userData?.data as any)?.email
  useEffect(() => { if (userPhone && !phone) setPhone(userPhone) }, [userPhone])
  useEffect(() => { if (userEmail && !email) setEmail(userEmail) }, [userEmail])

  // FIO from previous orders
  useEffect(() => {
    if (recipientName) return
    const lastRecipient = ordersData?.find(o => o.recipientName)?.recipientName
    if (lastRecipient) setRecipientName(lastRecipient)
  }, [ordersData])

  // --- Derived values ---
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const orderSubtotal = (cart?.totalAmount ?? 0) + deliveryCost
  const effectiveGiftCardAmount = giftCardApplied ? Math.min(giftCardAmount, orderSubtotal) : 0
  const totalWithDelivery = Math.max(0, orderSubtotal - effectiveGiftCardAmount)
  const isPaidByGiftCard = giftCardApplied && totalWithDelivery < 1
  const showDeliveryError = deliveryErrorCount > 0 && !canSubmitDelivery
  const showTermsError = termsErrorCount > 0 && !agreedToTerms

  // Force online payment for CDEK
  useEffect(() => {
    if (isCdekSelected) setPaymentType('online')
  }, [isCdekSelected])

  // Reset CDEK field errors on pickup
  useEffect(() => {
    if (!isCdekSelected) {
      setRecipientNameError(false)
      setPhoneError(false)
      setIsEditingPhone(false)
    }
  }, [isCdekSelected])

    // cartFingerprint used by useDeliveryRecalculation (imported below)

  // Auto-recalculate delivery cost when cart changes (shared hook)
  useDeliveryRecalculation(({ newCost }) => {
    if (newCost !== null) {
      showErrorToast(`Стоимость доставки пересчитана: ${newCost} ₽`)
    }
  })

  // --- Refs for stable callbacks (prevent MainButton flickering) ---
  const emailRef = useRef(email)
  const emailValidRef = useRef(emailValid)
  const recipientNameRef = useRef(recipientName)
  const phoneRef = useRef(phone)
  const isCdekRef = useRef(isCdek)
  const paymentTypeRef = useRef(paymentType)
  emailRef.current = email
  emailValidRef.current = emailValid
  recipientNameRef.current = recipientName
  phoneRef.current = phone
  isCdekRef.current = isCdek
  paymentTypeRef.current = paymentType

  const hapticFeedbackRef = useRef(hapticFeedback)
  const showErrorToastRef = useRef(showErrorToast)
  const createOrderMutateRef = useRef(createOrder.mutateAsync)
  hapticFeedbackRef.current = hapticFeedback
  showErrorToastRef.current = showErrorToast
  createOrderMutateRef.current = createOrder.mutateAsync

  const giftCardCodeRef = useRef(giftCardCode)
  const giftCardAppliedRef = useRef(giftCardApplied)
  giftCardCodeRef.current = giftCardCode
  giftCardAppliedRef.current = giftCardApplied

  // --- Theme ---
  const theme = webApp?.colorScheme || 'light'
  const buttonColors = MAIN_BUTTON_COLORS[theme]

  // --- BackButton: conditional handler for widget vs page ---
  const handleBack = useCallback(() => {
    if (useDeliveryStore.getState().widgetOpen) {
      useDeliveryStore.getState().closeWidget()
    } else {
      navigate({ to: '/cart' })
    }
  }, [navigate])
  useBackButton(true, handleBack)

  // --- MainButton text ---
  const mainButtonText = !canSubmitDelivery && !isCdekSelected
    ? 'Выберите способ получения'
    : isCdekSelected && !isCdek
      ? 'Выберите пункт СДЭК'
      : !canSubmitDelivery
        ? 'Укажите адрес доставки'
        : !agreedToTerms
          ? 'Примите условия'
          : isPaidByGiftCard
            ? 'Оформить — оплачено сертификатом'
            : `${paymentType === 'cash' ? 'Оформить заказ' : 'Перейти к оплате'} · ${formatPrice(totalWithDelivery)}`

  // --- Submit order ---
  const handleCheckout = useCallback(async () => {
    const dr = getDeliveryResult(useDeliveryStore.getState())
    if (!cart || cart.items.length === 0 || !dr) return

    if (dr.type !== 'pickup' && !dr.pvzInfo) {
      showErrorToastRef.current('Выберите пункт выдачи СДЭК')
      hapticFeedbackRef.current.notificationOccurred('error')
      return
    }

    try {
      const orderData: Parameters<typeof createOrderMutateRef.current>[0] = {
        deliveryType: dr.type,
        shippingComment: commentRef.current.trim() || undefined,
        phone: phoneRef.current.trim() || undefined,
        email: emailRef.current.trim() || undefined,
        recipientName: isCdekRef.current ? recipientNameRef.current.trim() || undefined : undefined,
        paymentType: paymentTypeRef.current,
        giftCardCode: giftCardAppliedRef.current ? giftCardCodeRef.current.trim() : undefined,
      }

      if (dr.type !== 'pickup' && dr.pvzInfo) {
        const pvzInfo = dr.pvzInfo
        orderData.cdek = {
          pointCode: pvzInfo.pointCode,
          pointName: pvzInfo.pointName,
          pointAddress: pvzInfo.pointAddress,
          pointCity: pvzInfo.pointCity,
          pointWorkTime: pvzInfo.pointWorkTime,
          pointType: pvzInfo.type,
          deliveryCost: pvzInfo.cost,
          minDays: pvzInfo.minDays,
          maxDays: pvzInfo.maxDays,
          tariffCode: pvzInfo.tariffCode,
          latitude: pvzInfo.latitude,
          longitude: pvzInfo.longitude,
        }
        if (dr.type === 'cdek_door' && dr.doorAddress) {
          orderData.cdek.doorStreet = dr.doorAddress.street
          orderData.cdek.doorHouse = dr.doorAddress.house
          orderData.cdek.doorBuilding = dr.doorAddress.building
          orderData.cdek.doorApartment = dr.doorAddress.apartment
          orderData.cdek.doorEntrance = dr.doorAddress.entrance
          orderData.cdek.doorFloor = dr.doorAddress.floor
          orderData.cdek.doorIntercom = dr.doorAddress.intercom
        }
      }

      const order = await createOrderMutateRef.current(orderData)
      setOrderCreated(true)
      hapticFeedbackRef.current.notificationOccurred('success')

      if (order.paymentUrl) {
        if (window.Telegram?.WebApp?.openLink) {
          window.Telegram.WebApp.openLink(order.paymentUrl)
        } else {
          window.open(order.paymentUrl, '_blank')
        }
        // Переходим на страницу заказа — статус оплаты обновится через webhook
        navigate({ to: '/order/$orderId', params: { orderId: order.id } })
      } else {
        navigate({ to: '/order-success', search: { orderId: order.id } })
      }
    } catch (error) {
      hapticFeedbackRef.current.notificationOccurred('error')
      const errorMessage = error instanceof Error ? error.message : 'Не удалось создать заказ'
      showErrorToastRef.current(errorMessage)
    }
  }, [cart, navigate])

  // --- MainButton click handler ---
  const handleMainButtonClick = useCallback(() => {
    if (!canSubmitDelivery && !isCdekSelected) {
      setDeliveryErrorCount(c => c + 1)
      hapticFeedbackRef.current.notificationOccurred('error')
      document.getElementById('delivery-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (isCdekSelected && !selectIsCdek(useDeliveryStore.getState())) {
      hapticFeedbackRef.current.notificationOccurred('warning')
      return
    }
    if (!selectCanSubmitDelivery(useDeliveryStore.getState())) {
      hapticFeedbackRef.current.notificationOccurred('warning')
      document.getElementById('delivery-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (paymentTypeRef.current !== 'cash' && !emailValidRef.current) {
      setEmailError(true)
      hapticFeedbackRef.current.notificationOccurred('error')
      document.getElementById('email-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (isCdekRef.current && recipientNameRef.current.trim().split(/\s+/).filter(Boolean).length < 2) {
      setRecipientNameError(true)
      hapticFeedbackRef.current.notificationOccurred('error')
      document.getElementById('recipient-name-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (isCdekRef.current && phoneRef.current.replace(/\D/g, '').length < 10) {
      setPhoneError(true)
      hapticFeedbackRef.current.notificationOccurred('error')
      document.getElementById('phone-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (!agreedToTerms) {
      setTermsErrorCount(c => c + 1)
      hapticFeedbackRef.current.notificationOccurred('error')
      document.getElementById('terms-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    handleCheckout()
  }, [agreedToTerms, canSubmitDelivery, isCdekSelected, handleCheckout])

  // --- MainButton ---
  const mainButtonOptions = useMemo(() => {
    if (!hasCart || widgetOpen) return null
    return {
      text: mainButtonText,
      onClick: handleMainButtonClick,
      color: buttonColors.color,
      textColor: buttonColors.textColor,
      isActive: true,
      isLoading: createOrder.isPending,
    }
  }, [hasCart, widgetOpen, mainButtonText, handleMainButtonClick, buttonColors, createOrder.isPending])

  useMainButton(mainButtonOptions)

  // --- Loading / redirect states ---
  if (orderCreated) {
    return (
      <AppShell showBottomNav>
        <div className="flex-1 flex items-center justify-center bg-paper dark:bg-dark-bg">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Оформляем заказ...</p>
          </div>
        </div>
        <BottomNav />
      </AppShell>
    )
  }

  if (!cartLoading && cart && cart.items.length === 0) {
    navigate({ to: '/cart' })
    return null
  }

  if (cartLoading) {
    return (
      <AppShell showBottomNav>
        <div className="flex-1 overflow-y-auto bg-paper dark:bg-dark-bg pt-safe">
          <div className="container mx-auto px-4 py-6 space-y-6">
            <div className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="flex-1">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-24 mt-1" />
              </div>
            </div>
            <Skeleton className="h-24 w-full rounded-2xl" />
            <Skeleton className="h-64 w-full rounded-2xl" />
          </div>
        </div>
        <BottomNav />
      </AppShell>
    )
  }

  if (!cart) return null

  return (
    <AppShell showBottomNav>
      <div className="flex-1 overflow-y-auto bg-paper dark:bg-dark-bg">

        {/* Compact header */}
        <div className="bg-white dark:bg-dark-card pt-safe px-4 pt-4 pb-3 border-b border-silk/30 dark:border-dark-border">
          <h1 className="text-[18px] font-bold text-ink dark:text-white">Оформление заказа</h1>
        </div>

        {/* Content — single column, tight padding */}
        <div className="px-4 py-3 space-y-3 pb-6">

          {/* Order summary — always visible, compact */}
          <OrderSummary cart={cart} isExpanded={isOrderExpanded} onToggle={() => {
            hapticFeedback.impactOccurred('light')
            setIsOrderExpanded(!isOrderExpanded)
          }} />

          {/* Delivery method */}
          <Section
            id="delivery-section"
            title="Способ получения"
            error={showDeliveryError ? 'Выберите способ' : undefined}
          >
            <CdekDeliverySection pickupAddress={pickupAddress || 'ул. Советская, 15'} />
          </Section>

          {/* Payment method — pickup only */}
          {!isCdekSelected && (
            <Section title="Оплата">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPaymentType('online')}
                  className={cn(
                    'flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-colors',
                    paymentType === 'online'
                      ? 'bg-primary text-white'
                      : 'bg-paper dark:bg-dark-bg text-gray-500 dark:text-gray-400 border border-silk/60 dark:border-dark-border',
                  )}
                >
                  <CreditCard className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                  Онлайн
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentType('cash')}
                  className={cn(
                    'flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-colors',
                    paymentType === 'cash'
                      ? 'bg-primary text-white'
                      : 'bg-paper dark:bg-dark-bg text-gray-500 dark:text-gray-400 border border-silk/60 dark:border-dark-border',
                  )}
                >
                  <Banknote className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                  При получении
                </button>
              </div>
            </Section>
          )}

          {/* Email */}
          {paymentType !== 'cash' && (
            <div id="email-section" className="bg-white dark:bg-dark-card rounded-xl border border-silk/50 dark:border-dark-border px-4 py-3">
              <Input
                id="email"
                label="Email для чека"
                placeholder="example@mail.ru"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(false) }}
                state={emailError ? 'error' : 'default'}
                errorMessage={emailError ? 'Укажите корректный email' : undefined}
                hint="ЮKасса отправит фискальный чек"
                leftIcon={<Mail className="h-4 w-4" />}
                required
                autoComplete="email"
              />
            </div>
          )}

          {/* Recipient name — CDEK only */}
          {isCdek && (
            <div id="recipient-name-section" className="bg-white dark:bg-dark-card rounded-xl border border-silk/50 dark:border-dark-border px-4 py-3">
              <Input
                id="recipient-name"
                label="ФИО получателя"
                placeholder="Иванов Иван Иванович"
                value={recipientName}
                onChange={(e) => { setRecipientName(e.target.value); if (recipientNameError) setRecipientNameError(false) }}
                state={recipientNameError ? 'error' : 'default'}
                errorMessage={recipientNameError ? 'Укажите полное ФИО как в паспорте' : undefined}
                hint="Фамилия Имя Отчество — для СДЭК"
                leftIcon={<User className="h-4 w-4" />}
                required
                autoComplete="name"
              />
            </div>
          )}

          {/* Phone — CDEK only */}
          {isCdek && (
            <div id="phone-section" className="bg-white dark:bg-dark-card rounded-xl border border-silk/50 dark:border-dark-border px-4 py-3">
              {phone && !isEditingPhone ? (
                <div className="flex items-center justify-between py-0.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center shrink-0">
                      <Phone className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">Телефон</p>
                      <p className="text-[14px] font-medium text-ink dark:text-white">{phone}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setIsEditingPhone(true)} className="text-[13px] font-medium text-primary">
                    Изменить
                  </button>
                </div>
              ) : (
                <Input
                  id="phone"
                  label="Телефон получателя"
                  type="tel"
                  placeholder="+7 (999) 123-45-67"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); if (phoneError) setPhoneError(false) }}
                  onBlur={() => { if (phone.replace(/\D/g, '').length >= 10) setIsEditingPhone(false) }}
                  state={phoneError ? 'error' : 'default'}
                  errorMessage={phoneError ? 'Укажите номер телефона' : undefined}
                  hint="СДЭК пришлёт уведомления"
                  leftIcon={<Phone className="h-4 w-4" />}
                  required
                  autoComplete="tel"
                  inputMode="tel"
                  autoFocus={isEditingPhone}
                />
              )}
            </div>
          )}

          {/* Order totals — shown after delivery selected */}
          {(canSubmitDelivery || giftCardApplied) && (
            <div className="bg-white dark:bg-dark-card rounded-xl border border-silk/50 dark:border-dark-border px-4 py-3">
              <OrderTotals
                cartTotal={cart.totalAmount}
                deliveryCost={deliveryCost}
                giftCardApplied={giftCardApplied}
                effectiveGiftCardAmount={effectiveGiftCardAmount}
                totalWithDelivery={totalWithDelivery}
              />
            </div>
          )}

          {/* Gift card */}
          <GiftCardSection
            giftCardCode={giftCardCode}
            giftCardApplied={giftCardApplied}
            giftCardBalance={giftCardBalance}
            effectiveGiftCardAmount={effectiveGiftCardAmount}
            giftCardExpanded={giftCardExpanded}
            giftCardError={giftCardError}
            giftCardLoading={giftCardLoading}
            orderSubtotal={orderSubtotal}
            hapticFeedback={hapticFeedback}
            onCodeChange={setGiftCardCode}
            onApply={(balance, discount) => {
              setGiftCardBalance(balance)
              setGiftCardAmount(discount)
              setGiftCardApplied(true)
            }}
            onReset={() => {
              setGiftCardApplied(false)
              setGiftCardAmount(0)
              setGiftCardBalance(0)
              setGiftCardCode('')
              setGiftCardError('')
            }}
            onExpand={() => setGiftCardExpanded(true)}
            onError={setGiftCardError}
            onLoading={setGiftCardLoading}
          />

          {/* Comment */}
          <OrderCommentSection onChange={(val) => { commentRef.current = val }} />

          {/* Terms */}
          <div
            id="terms-section"
            className={cn(
              'bg-white dark:bg-dark-card rounded-xl border px-4 py-3',
              showTermsError ? 'border-red-400 dark:border-red-500 animate-shake' : 'border-silk/50 dark:border-dark-border',
            )}
            key={showTermsError ? `terms-error-${termsErrorCount}` : 'terms'}
          >
            <div className="flex items-start gap-3">
              <Checkbox
                id="agree-to-terms"
                checked={agreedToTerms}
                onCheckedChange={(checked) => { hapticFeedback.selectionChanged(); setAgreedToTerms(!!checked) }}
                className="mt-0.5"
              />
              <label htmlFor="agree-to-terms" className="text-[12px] text-gray-600 dark:text-gray-300 leading-relaxed cursor-pointer">
                Согласен на обработку персональных данных и принимаю{' '}
                <Link to="/privacy" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                  Политику конфиденциальности
                </Link>{' '}
                и{' '}
                <Link to="/offer" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                  Публичную оферту
                </Link>
              </label>
            </div>
            {showTermsError && (
              <p className="mt-2 text-[12px] text-red-500 dark:text-red-400 flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" />
                Необходимо принять условия
              </p>
            )}
          </div>

          {/* Payment note */}
          <div className={cn(
            'flex items-center gap-2 px-3 py-2.5 rounded-xl text-[12px]',
            paymentType === 'cash'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
              : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
          )}>
            <Shield className="h-3.5 w-3.5 shrink-0" />
            {paymentType === 'cash'
              ? 'Оплата наличными или картой при получении'
              : 'Перейдёте на защищённую страницу оплаты ЮKassa'}
          </div>

        </div>{/* /content */}
        <div className="bottom-nav-spacer" />
      </div>

      {/* Full-screen CDEK widget overlay */}
      <CdekDeliveryPicker />

      <BottomNav />
    </AppShell>
  )
}

// ============================================================================
// Section wrapper
// ============================================================================

function Section({
  id,
  title,
  error,
  children,
}: {
  id?: string
  title: string
  error?: string
  children: ReactNode
}) {
  return (
    <div
      id={id}
      className="bg-white dark:bg-dark-card rounded-xl border border-silk/50 dark:border-dark-border overflow-hidden"
    >
      <div className="px-4 py-2.5 border-b border-silk/30 dark:border-dark-border flex items-center justify-between">
        <span className="text-[13px] font-semibold text-ink dark:text-white">{title}</span>
        {error && (
          <span className="text-[12px] text-red-500 dark:text-red-400 animate-in fade-in slide-in-from-right-2 duration-200">
            {error}
          </span>
        )}
      </div>
      <div className="p-3">{children}</div>
    </div>
  )
}

// ============================================================================
// Order Summary (extracted to prevent re-renders)
// ============================================================================

const OrderSummary = memo(function OrderSummary({
  cart,
  isExpanded,
  onToggle,
}: {
  cart: any
  isExpanded: boolean
  onToggle: () => void
}) {
  const getItemsText = (count: number) => {
    if (count === 1) return 'товар'
    if (count >= 2 && count <= 4) return 'товара'
    return 'товаров'
  }

  return (
    <div className="bg-white dark:bg-dark-card rounded-xl border border-silk/50 dark:border-dark-border overflow-hidden">
      <button type="button" onClick={onToggle} className="w-full px-4 py-3 flex items-center gap-3">
        <div className="flex-1 text-left">
          <div className="text-[14px] font-semibold text-ink dark:text-white">
            {cart.itemsCount} {getItemsText(cart.itemsCount)} · {formatPrice(cart.totalAmount)}
          </div>
        </div>
        {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>

      {isExpanded && (
        <div className="border-t border-silk/30 dark:border-dark-border px-4 py-3 space-y-3">
          {cart.items.map((item: any) => {
            const productName = item.product?.name || 'Товар'
            const productImage = item.product?.imageUrl || '/placeholder-product.svg'
            const price = item.product?.isOnSale && item.product?.salePriceAmount
              ? item.product.salePriceAmount
              : (item.product?.priceAmount || 0)
            const priceResult = item.productType === 'tea' && item.weight
              ? calculateTeaPrice({
                  pricePerGram: item.product?.priceAmount || 0,
                  weight: item.weight,
                  quantity: item.quantity,
                  cakePriceAmount: item.product?.cakePriceAmount,
                  cakeWeight: item.product?.cakeWeight,
                })
              : null
            const subtotal = priceResult?.subtotal ?? (price * item.quantity)

            return (
              <div key={item.id} className="flex items-center gap-3">
                <ProductImage src={productImage} alt={productName} size="sm" className="w-12 h-12 rounded-lg" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink dark:text-white line-clamp-1">{productName}</p>
                  {item.productType !== 'tea' && item.product?.sku && (
                    <p className="text-[10px] font-mono text-gray-400 dark:text-gray-500">Арт. {item.product.sku}</p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {item.weight && formatWeight(item.weight)} x {item.quantity}
                  </p>
                </div>
                <p className="text-sm font-semibold text-primary dark:text-primary-light tabular-nums">
                  {formatPrice(subtotal)}
                </p>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
})

// ============================================================================
// Gift Card Section (extracted)
// ============================================================================

const GiftCardSection = memo(function GiftCardSection({
  giftCardCode,
  giftCardApplied,
  giftCardBalance,
  effectiveGiftCardAmount,
  giftCardExpanded,
  giftCardError,
  giftCardLoading,
  orderSubtotal,
  hapticFeedback,
  onCodeChange,
  onApply,
  onReset,
  onExpand,
  onError,
  onLoading,
}: {
  giftCardCode: string
  giftCardApplied: boolean
  giftCardBalance: number
  effectiveGiftCardAmount: number
  giftCardExpanded: boolean
  giftCardError: string
  giftCardLoading: boolean
  orderSubtotal: number
  hapticFeedback: any
  onCodeChange: (code: string) => void
  onApply: (balance: number, discount: number) => void
  onReset: () => void
  onExpand: () => void
  onError: (msg: string) => void
  onLoading: (loading: boolean) => void
}) {
  const handleCheck = async () => {
    const code = giftCardCode.trim().toUpperCase()
    if (!code) return
    onLoading(true)
    onError('')
    try {
      const res = await apiClient<{ success: boolean; data: { balanceAmount: number } }>(
        `/api/public/gift-cards/check/${encodeURIComponent(code)}`,
      )
      if (!res.success) throw new Error('Не найден')
      const balance = res.data.balanceAmount
      const discount = Math.min(balance, orderSubtotal)
      onApply(balance, discount)
      hapticFeedback.notificationOccurred('success')
    } catch (err: any) {
      onError(err.message || 'Сертификат не найден')
      hapticFeedback.notificationOccurred('error')
    } finally {
      onLoading(false)
    }
  }

  return (
    <div className="bg-white dark:bg-dark-card rounded-xl border border-silk/50 dark:border-dark-border px-4 py-3">
      {giftCardApplied ? (
        <div>
          <div className="flex items-center gap-3">
            <Check className="w-4 h-4 text-teal-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-teal-800 dark:text-teal-400">Сертификат применён</p>
              <p className="text-xs text-teal-600 dark:text-teal-500 font-mono">{giftCardCode}</p>
            </div>
            <button onClick={onReset} className="text-gray-400 hover:text-gray-600 p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-3 mt-2 text-[11px] text-teal-600 dark:text-teal-500">
            <span>Баланс: {formatPrice(giftCardBalance)}</span>
            <span>Спишется: {formatPrice(effectiveGiftCardAmount)}</span>
            <span>Останется: {formatPrice(giftCardBalance - effectiveGiftCardAmount)}</span>
          </div>
        </div>
      ) : !giftCardExpanded ? (
        <button onClick={onExpand} className="text-sm text-primary font-medium">
          <Gift className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Есть подарочный сертификат?
        </button>
      ) : (
        <div>
          <div className="flex gap-2">
            <input
              type="text"
              value={giftCardCode}
              onChange={(e) => { onCodeChange(e.target.value.toUpperCase()); onError('') }}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              className={cn(
                'flex-1 px-3 py-2 rounded-xl border text-sm font-mono tracking-wider outline-none transition-colors',
                'bg-paper dark:bg-dark-bg text-ink dark:text-dark-text',
                giftCardError ? 'border-red-300' : 'border-silk dark:border-dark-border',
              )}
            />
            <button
              onClick={handleCheck}
              disabled={!giftCardCode.trim() || giftCardLoading}
              className="px-4 py-2 rounded-xl bg-ink dark:bg-white text-white dark:text-ink text-sm font-medium disabled:opacity-50"
            >
              {giftCardLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ок'}
            </button>
          </div>
          {giftCardError && <p className="text-xs text-red-500 mt-1.5">{giftCardError}</p>}
        </div>
      )}
    </div>
  )
})

// ============================================================================
// Comment Section (isolated to prevent re-renders of heavy page)
// ============================================================================

const OrderCommentSection = memo(({ onChange }: { onChange: (val: string) => void }) => {
  const [value, setValue] = useState('')
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newVal = e.target.value
    setValue(newVal)
    onChange(newVal)
  }

  return (
    <div className="bg-white dark:bg-dark-card rounded-xl border border-silk/50 dark:border-dark-border px-4 py-3 space-y-2">
      <label htmlFor="comment" className="text-[13px] font-semibold text-ink dark:text-white">
        Комментарий к заказу
      </label>
      <Textarea
        id="comment"
        placeholder="Особые пожелания к заказу..."
        rows={3}
        value={value}
        onChange={handleChange}
        maxLength={500}
        showCounter
      />
    </div>
  )
})
OrderCommentSection.displayName = 'OrderCommentSection'

// ============================================================================
// Order Totals (extracted — используется в левой колонке mobile и правой на tablet)
// ============================================================================

function OrderTotals({
  cartTotal,
  deliveryCost,
  giftCardApplied,
  effectiveGiftCardAmount,
  totalWithDelivery,
}: {
  cartTotal: number
  deliveryCost: number
  giftCardApplied: boolean
  effectiveGiftCardAmount: number
  totalWithDelivery: number
}) {
  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <span className="text-gray-500 dark:text-gray-400">Товары</span>
        <span className="font-medium text-ink dark:text-white">{formatPrice(cartTotal)}</span>
      </div>
      {deliveryCost > 0 && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-500 dark:text-gray-400">Доставка СДЭК</span>
          <span className="font-medium text-ink dark:text-white">~{formatPrice(deliveryCost)}</span>
        </div>
      )}
      {giftCardApplied && effectiveGiftCardAmount > 0 && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <Gift className="w-3.5 h-3.5 text-teal-600" /> Сертификат
          </span>
          <span className="font-medium text-teal-600">-{formatPrice(effectiveGiftCardAmount)}</span>
        </div>
      )}
      <div className="pt-3 border-t border-silk/50 dark:border-dark-border flex justify-between items-center">
        <span className="font-semibold text-ink dark:text-white">Итого</span>
        <span className="text-lg font-bold text-primary">
          {totalWithDelivery === 0 ? 'Оплачено сертификатом' : formatPrice(totalWithDelivery)}
        </span>
      </div>
    </>
  )
}
